/**
 * The offer window.
 *
 * Not a valuation: a range to negotiate within, built from four methods a
 * buyer can check — recent comparable sales, an automated estimate, the
 * property's own last sale carried forward, and the closest-matched sales
 * valued on their own (comparative market analysis) — plus the indicative price, which
 * is shown as an input with its weight, never hidden inside the figure.
 *
 * Backtested on 777 held-out sales (2 Oct 2026): with an agent's guide as the
 * indicative price the midpoint's typical error was 7.7%, the worst tenth 20%,
 * and the window held the eventual sale price 69% of the time. The spec it
 * replaced published ±5% windows that held it 33% of the time.
 *
 * The price rating compares the indicative price with what the methods alone
 * point to. 70 means they match — a fair price reads as fair, not as a mark of
 * fifty — and paying over the evidence costs points faster than paying under
 * earns them, because overpaying is the buyer's real risk.
 */

import { isStrataAddress } from './geometry.ts';
import type { AgentQuoting, Avm, Comparable, CuratedComp, PriceGrowth, RepeatSalePoint, SaleHistoryRow, SuburbSale } from './microburbs.ts';

export type Confidence = 'High' | 'Medium' | 'Low';
export type MethodKey = 'M1' | 'M2' | 'M3' | 'M4';

export type OfferMethod = {
  key: MethodKey;
  name: string;
  mid: number | null;
  confidence: Confidence;
  why: string;
  /** Share of the midpoint this method carries, 0–1. 0 when it produced no figure. */
  weight: number;
  /**
   * The method produced a figure but it was set aside. The figure itself is
   * never shown: an estimate too far out to use only looks wrong beside the
   * others (65 Ninth Ave: $1.12m against $1.91m comparables).
   */
  setAside?: true;
};

export type OfferWindow = {
  mid: number;
  low: number;
  high: number;
  /** Half-width of the window as a fraction of the midpoint. */
  halfWidth: number;
  confidence: Confidence;
  methods: OfferMethod[];
  /** The conf-weighted figure from the methods alone — what the price is rated against. */
  evidence: number | null;
  indicative: {
    price: number;
    source: 'agent' | 'buyer';
    /** The price after the agency's own underquoting record is allowed for. */
    used: number;
    weight: number;
    note: string | null;
  } | null;
  stale: { days: number; suburbDays: number } | null;
  /** The midpoint was held at the indicative price because the market has declined it. */
  capped: boolean;
  rating: { score: number; label: RatingLabel; reason: string } | null;
  /**
   * What any offer would rate, by the same formula: the evidence it is measured
   * against and whether that evidence is thin. Lets a buyer rate their own offer.
   */
  rate: RateBasis | null;
  /**
   * With no asking or indicative price to rate, the rating is given for offers
   * instead: the price bands that earn each label.
   */
  offerBands: { label: RatingLabel; from: number | null; to: number | null }[] | null;
  /** Plain reasons for the window, in reading order. */
  why: string[];
  /** The settled sales behind Method 1, as adjusted to this property, cheapest first. */
  comparables: OfferComparable[];
  /** The property's own last settlement that Method 3 carried forward. */
  lastSale: { price: number; date: string } | null;
};

export type OfferComparable = {
  address: string;
  price: number;
  adjusted: number;
  date: string | null;
  distKm: number | null;
  beds: number | null;
  baths: number | null;
  landSqm: number | null;
};

export type RateBasis = { evidence: number; thin: boolean };

export type RatingLabel = 'Strong value' | 'Fair price' | 'Priced high' | 'Overpriced';

/**
 * What the buyer told us about the house itself, which no sales feed can see.
 * Stored as enums. Skipped questions are null and adjust nothing.
 */
export type HouseAnswers = {
  finish?: 'needs_work' | 'original' | 'good' | 'updated' | 'high_spec' | 'new_build' | null;
  changed_since_sale?: 'yes' | 'no' | 'unknown' | null;
  extra?: 'none' | 'extension' | 'granny' | 'pool' | null;
  aspect?: 'ordinary' | 'main_road' | 'outlook' | 'water' | null;
  land_form?: 'flat' | 'sloping' | 'steep' | 'battleaxe' | null;
  site?: 'none' | 'possible' | 'approved' | null;
};

/**
 * Condition against the comparables, which are average-condition houses (the
 * feed carries no condition). 20% left a new build in New Farm (41 Bailey St,
 * asking $7m) at $4.97m against comps of $3.97m; raised 10 Oct 2026 on review.
 * Set where the adjusted figure lands in the comparables' own spread: 40% put
 * a new build above every nearby sale, 30% lands it just under the dearest
 * ($5.37m against $5.5m); high-spec at 30% sits in the top quarter, an update
 * at 25% above the middle. A judgement, not a backtest: no sale in the feed
 * says it was renovated. Additions (pool, extension, granny flat) stack on top.
 */
export const FINISH_PCT = { needs_work: -15, original: -8, good: 0, updated: 25, high_spec: 30, new_build: 30 } as const;
export const EXTRA_PCT = { none: 0, extension: 7, granny: 10, pool: 5 } as const;
export const ASPECT_PCT = { ordinary: 0, main_road: -8, outlook: 10, water: 15 } as const;
export const LAND_FORM_PCT = { flat: 0, sloping: -4, steep: -10, battleaxe: -7 } as const;
export const SITE_PCT = { none: 0, possible: 0, approved: 12 } as const;
const HOUSE_LABEL: Record<string, string> = {
  needs_work: 'Needs work', original: 'Original', updated: 'Updated', high_spec: 'High-spec renovation', new_build: 'New or rebuilt',
  extension: 'Extra living / extension', granny: 'Granny flat or secondary dwelling', pool: 'Pool',
  main_road: 'Main road or rail', outlook: 'Elevated outlook', water: 'Water',
  sloping: 'Sloping', steep: 'Steep', battleaxe: 'Battle-axe or shared drive', approved: 'Approved plans',
};
/** The combined allowance never goes past this either way: room for a pool or extension on top of a new build. */
const HOUSE_CAP = 50;
/** "Might be splittable" lifts the top of the window to at least this far above the midpoint. */
const SPLIT_TOP = 0.12;

/**
 * The buyer's answers as one percentage on Method 1. Discounts and premiums
 * net out before the ±50% cap; a steep block with an outlook or water view
 * carries no slope penalty, because the view is the reason for the slope.
 */
export function houseAllowance(h: HouseAnswers | null | undefined, isHouse: boolean): { pct: number; capped: boolean; parts: { label: string; pct: number }[] } {
  if (!h) return { pct: 0, capped: false, parts: [] };
  const parts: { label: string; pct: number }[] = [];
  const add = (key: string | null | undefined, pct: number) => {
    if (key && pct !== 0) parts.push({ label: HOUSE_LABEL[key] ?? key, pct });
  };
  add(h.finish, h.finish ? FINISH_PCT[h.finish] : 0);
  // A unit has no pool or granny flat of its own; either answer is ignored.
  add(h.extra, h.extra ? ((h.extra === 'pool' || h.extra === 'granny') && !isHouse ? 0 : EXTRA_PCT[h.extra]) : 0);
  add(h.aspect, h.aspect ? ASPECT_PCT[h.aspect] : 0);
  const viewExcusesSlope = h.land_form === 'steep' && (h.aspect === 'outlook' || h.aspect === 'water');
  add(h.land_form, h.land_form && !viewExcusesSlope ? LAND_FORM_PCT[h.land_form] : 0);
  add(h.site, h.site ? SITE_PCT[h.site] : 0);
  const raw = parts.reduce((n, p) => n + p.pct, 0);
  const pct = clip(raw, -HOUSE_CAP, HOUSE_CAP);
  return { pct, capped: pct !== raw, parts };
}

export type OfferInput = {
  subject: {
    /** Its own address id, so its own sales never count as comparables. */
    gnafId?: string;
    address: string;
    isUnit: boolean;
    beds: number | null;
    baths: number | null;
    landSqm: number | null;
    lat: number | null;
    lng: number | null;
  };
  comps: Comparable[];
  curatedComps: CuratedComp[];
  /** The suburb sales feed (and a neighbour's), where the report bought it. */
  suburbSales?: SuburbSale[];
  compLand: Record<string, number | null>;
  avm: Avm | null;
  saleHistory: SaleHistoryRow[];
  /**
   * The suburb's resales, bought with the market reads. The property's own
   * sale history often records a settlement with no price; these carry it.
   */
  repeatSales?: RepeatSalePoint[];
  priceGrowth: PriceGrowth['house'] | null;
  /** The figure the buyer gave at intake, or the advertised price where they gave none. */
  indicative: { price: number; source: 'agent' | 'buyer' } | null;
  daysOnMarket: number | null;
  suburbDaysOnMarket: number | null;
  agentQuoting: AgentQuoting | null;
  newBuild?: boolean;
  /** The buyer's answers about the house, from intake. */
  house?: HouseAnswers | null;
  now: Date;
};

// ── Constants ──────────────────────────────────────────────────────────────

/** Share of the midpoint the indicative price carries, by where it came from. */
export const INDICATIVE_SHARE = { agent: 0.55, buyer: 0.25, stale: 0.2 } as const;
/** A listing this many times the suburb's usual selling time has been declined. */
export const STALE_MULTIPLE = 1.5;
const CONF_WEIGHT: Record<Confidence, number> = { High: 3, Medium: 2, Low: 1 };
const HALF_MIN = 0.1;
const HALF_MAX = 0.15;
const HALF_MAX_LOW = 0.2;
/** The least half-width when the window rests on adjusted comparables alone (see describedOnly). */
const DESCRIBED_HALF = 0.12;
/**
 * Half-widths with no price to anchor on, by strength of evidence. Without one
 * the methods' typical error doubles (13% against 7.7%); on 564 held-out sales
 * these held the sale price 7 times in 10 at each level.
 */
const NO_PRICE_HALF = { thin: 0.24, medium: 0.18, strong: 0.12 } as const;
const NO_PRICE_HALF_MAX = 0.25;
const MIN_COMPS = 3;
/**
 * Method 4 needs this many matched sales from the provider's comparable set.
 * Held out on 78 real sales (14 Oct 2026), adding it beside Method 1 took the
 * typical miss from 14.3% to 14.0% and the share within 10% from 39% to 43%.
 */
const CMA_MIN = 4;
/**
 * A land band must hold this many sales before it is used on its own. Three let
 * 114 Heal St rest on three sales each lifted the full 25% for land, one of them
 * $6.2m; on 1,968 held-out sales five was as accurate and never that fragile.
 */
const BAND_MIN = 5;
const MAX_COMPS = 8;
const RECENCY_HALFLIFE_YEARS = 4;
const LAND_RATIO = [0.8, 1.25] as const;
const BATH_HEDONIC = 0.025;
/** A method further than this from both M1 and the indicative price is kept, but marked Low. */
const FAR_PCT = 0.15;
/**
 * An automated estimate this far from the comparable sales is shown but not
 * used. 1405/30 Festival Place: the model said $784k for a 3-bed whose
 * building's 3-beds sold for $1.18m–$1.58m; even at the lowest weight it
 * dragged the window down.
 */
const AVM_DROP_PCT = 0.3;
const MIN_SALE_PRICE = 50_000;
/** Suburb-feed sales further than this from the property are not comparables. */
const FEED_RADIUS_KM = 1.5;
const YEAR_MS = 365.25 * 86_400_000;

// ── Helpers ────────────────────────────────────────────────────────────────

const clip = (x: number, lo: number, hi: number): number => Math.max(lo, Math.min(hi, x));
const pos = (v: unknown): number | null => {
  const n = Number(v);
  return Number.isFinite(n) && n > 0 ? n : null;
};
const yearsBetween = (date: string | null | undefined, now: Date): number => {
  const t = date ? Date.parse(date) : NaN;
  return Number.isFinite(t) ? Math.max(0, (now.getTime() - t) / YEAR_MS) : Infinity;
};
const short = (v: number): string =>
  v >= 1e6 ? `$${(Math.round(v / 1e4) / 100).toFixed(2).replace(/0$/, '')}m` : `$${Math.round(v / 1e3)}k`;
const pct = (v: number): string => `${Math.round(v * 100)}%`;

type Klass = 'house' | 'townhouse' | 'unit' | 'land' | 'unknown';
export function classOf(s: string | null | undefined): Klass {
  const t = (s ?? '').toLowerCase();
  if (/unit|apartment|flat|studio|stratum/.test(t)) return 'unit';
  if (/townhouse|terrace|duplex|villa/.test(t)) return 'townhouse';
  if (/vacant|land/.test(t)) return 'land';
  if (/house|dwelling|home|detached|freestanding/.test(t)) return 'house';
  return 'unknown';
}
/**
 * A sale's dwelling class, from its type and its address together. The suburb
 * feed calls strata townhouses and some units "Townhouse"; an address like
 * "Unit 4, 19 Annie Street" or "4/19 Annie Street" is a unit whatever the type
 * says. 21 Browne St, a house, was being compared with three of them.
 */
export function saleClass(propertyType: string | null | undefined, address: string | null | undefined): Klass {
  return address && isStrataAddress(address) ? 'unit' : classOf(propertyType);
}

const classMatch = (a: Klass, b: Klass): boolean =>
  a === b || ((a === 'house' || a === 'townhouse') && (b === 'house' || b === 'townhouse'));

/** Street types as the feeds abbreviate them, so "Place" and "PL" are one street. */
const STREET_TYPE: Record<string, string> = {
  street: 'st', road: 'rd', avenue: 'ave', av: 'ave', place: 'pl', terrace: 'tce', parade: 'pde', drive: 'dr',
  court: 'ct', crescent: 'cres', cr: 'cres', lane: 'ln', close: 'cl', highway: 'hwy', boulevard: 'bvd', blvd: 'bvd',
  esplanade: 'esp', circuit: 'cct', square: 'sq', grove: 'gr', way: 'wy', parkway: 'pkwy',
};

/**
 * "1/713 Brunswick Street, New Farm" and "UNIT 4, 713 BRUNSWICK ST" share a
 * scheme. The street type is folded to one spelling: the feeds wrote 20 Festival
 * Place as both "Place" and "PL", which split one building in two and left
 * 805/20 Festival Place, sold a month earlier, out of Unit 1405's comparables.
 */
export function schemeKey(address: string): string {
  return address
    .toLowerCase()
    .replace(/^\s*(unit|apt|apartment|u)\s*/, '')
    .replace(/^\s*\d+\w?\s*[/,]\s*/, '')
    .split(',')[0]
    .trim()
    .split(/\s+/)
    .map((w) => STREET_TYPE[w] ?? w)
    .join('')
    .replace(/[^a-z0-9]/g, '');
}

function km(aLat: number, aLng: number, bLat: number, bLng: number): number {
  const r = Math.PI / 180;
  const h =
    Math.sin(((bLat - aLat) * r) / 2) ** 2 +
    Math.cos(aLat * r) * Math.cos(bLat * r) * Math.sin(((bLng - aLng) * r) / 2) ** 2;
  return 12742 * Math.asin(Math.sqrt(h));
}

type Horizons = { '1yr': number; '3yr': number; '5yr': number; '10yr': number };

/** All four horizons, or nothing: a sale cannot be carried forward on a partial series. */
function horizons(g: PriceGrowth['house']): Horizons | null {
  if (!g) return null;
  const h = [g['1yr'], g['3yr'], g['5yr'], g['10yr']];
  return h.every((v) => typeof v === 'number' && Number.isFinite(v)) ? (g as Horizons) : null;
}

/** Cumulative growth over `y` years from the published 1/3/5/10-year horizons. */
export function indexPct(g: Horizons, y: number): number {
  if (!(y > 0) || !Number.isFinite(y)) return 0;
  const pts: [number, number][] = [
    [1, g['1yr']],
    [3, g['3yr']],
    [5, g['5yr']],
    [10, g['10yr']],
  ];
  if (y <= 1) return pts[0][1] * y;
  if (y >= 10) return pts[3][1];
  for (let i = 1; i < pts.length; i++) {
    if (y <= pts[i][0]) {
      const [a, ga] = pts[i - 1];
      const [b, gb] = pts[i];
      return ga + ((y - a) / (b - a)) * (gb - ga);
    }
  }
  return pts[3][1];
}

// ── Method 1: recent comparable sales ──────────────────────────────────────

type Pool = {
  address: string;
  price: number;
  date: string | null;
  dist: number | null;
  klass: Klass;
  beds: number | null;
  baths: number | null;
  land: number | null;
};

type M1 = {
  mid: number;
  n: number;
  confidence: Confidence;
  band: 'tight_25' | 'med_50' | 'all' | 'no_subject_area';
  recency: '36mo' | '60mo' | 'all';
  bathRule: 'exact' | '±1';
  unknownLand: number;
  bedsKnown: boolean;
  sales: OfferComparable[];
};

function buildPool(input: OfferInput): Pool[] {
  const { subject } = input;
  const out = new Map<string, Pool>();
  const own = (gnaf: string) => !!subject.gnafId && gnaf === subject.gnafId;
  for (const c of input.comps) {
    if (!(c.price > 0) || own(c.gnaf_pid)) continue;
    out.set(c.gnaf_pid, {
      address: c.address,
      price: c.price,
      date: c.date_sold ?? null,
      dist: Number.isFinite(c.dist_km) ? c.dist_km : null,
      klass: saleClass(c.property_type, c.address),
      beds: c.bedrooms,
      baths: c.bathrooms,
      land: pos((c as Comparable & { land_size?: unknown }).land_size) ?? pos(input.compLand[c.gnaf_pid]),
    });
  }
  // The provider's own feature-matched set widens the pool where the radius is thin.
  for (const c of input.curatedComps) {
    const price = pos(c.price);
    if (!price || own(c.gnaf_pid)) continue;
    const have = out.get(c.gnaf_pid);
    if (have) {
      have.land ??= pos(c.land_size);
      continue;
    }
    const geo = c as CuratedComp & { latitude?: number; longitude?: number };
    out.set(c.gnaf_pid, {
      address: c.street,
      price,
      date: c.date_sold ?? null,
      dist:
        subject.lat != null && subject.lng != null && geo.latitude != null && geo.longitude != null
          ? km(subject.lat, subject.lng, geo.latitude, geo.longitude)
          : null,
      klass: saleClass(c.property_type, c.street),
      beds: c.bedrooms,
      baths: c.bathrooms,
      land: pos(c.land_size),
    });
  }
  // Two years of the suburb's own sales, the deepest pool and the one that
  // carries land sizes. The nearby feed alone reached back three or four months.
  if (subject.lat != null && subject.lng != null) {
    for (const r of input.suburbSales ?? []) {
      const price = pos(r.sold_price);
      if (!price || own(r.gnaf_pid) || r.lat == null || r.lng == null) continue;
      const dist = km(subject.lat, subject.lng, r.lat, r.lng);
      if (dist > FEED_RADIUS_KM) continue;
      const have = out.get(r.gnaf_pid);
      if (have && have.date === r.sold_date) {
        have.land ??= pos(r.land_size) ?? pos(input.compLand[r.gnaf_pid]);
        continue;
      }
      // A property that sold twice is two sales; key the later one apart.
      out.set(have ? `${r.gnaf_pid}|${r.sold_date}` : r.gnaf_pid, {
        address: r.address,
        price,
        date: r.sold_date ?? null,
        dist,
        klass: saleClass(r.property_type, r.address),
        beds: r.bedrooms,
        baths: r.bathrooms,
        land: pos(r.land_size) ?? pos(input.compLand[r.gnaf_pid]),
      });
    }
  }
  return [...out.values()];
}

export function method1(input: OfferInput): M1 | null {
  const { subject, now } = input;
  const klass: Klass = subject.isUnit ? 'unit' : 'house';
  // With no published bedroom count, match on dwelling type and land alone —
  // weaker, so it can only ever carry a lower weight. Units have no land to
  // match on, and a one-bed and a three-bed unit are different markets, so a
  // unit with no bedroom count gets no Method 1 at all. (47 Meymot St is
  // classed a unit with no bedrooms on a 399 m² lot of a street of houses;
  // type alone valued it at $476k against nearby units.)
  const bedsKnown = subject.beds != null;
  if (!bedsKnown && (subject.isUnit || !subject.landSqm)) return null;
  let f = buildPool(input).filter(
    (c) => classMatch(c.klass, klass) && (!bedsKnown || (c.beds != null && c.beds === subject.beds)),
  );

  // Bathrooms: exact where enough match, else ±1 with a per-bathroom adjustment.
  let bathRule: M1['bathRule'] = 'exact';
  if (subject.baths) {
    const sb = Math.round(subject.baths);
    const exact = f.filter((c) => c.baths != null && Math.round(c.baths) === sb);
    if (exact.length < MIN_COMPS) {
      f = f.filter((c) => c.baths != null && Math.abs(Math.round(c.baths) - sb) <= 1);
      bathRule = '±1';
    } else f = exact;
  }

  // Land: tightest band holding three sales. Sales with no land figure are kept
  // at half weight rather than dropped — on most streets only a dozen of fifty
  // nearby sales come back with a lot size.
  let band: M1['band'] = 'no_subject_area';
  if (!subject.isUnit && subject.landSqm) {
    const land = subject.landSqm;
    const unknown = f.filter((c) => !c.land);
    const known = f.filter((c) => c.land);
    const tight = known.filter((c) => Math.abs(c.land! / land - 1) <= 0.25);
    const med = known.filter((c) => Math.abs(c.land! / land - 1) <= 0.5);
    if (tight.length >= BAND_MIN) [f, band] = [tight, 'tight_25'];
    else if (med.length >= BAND_MIN) [f, band] = [med, 'med_50'];
    else [f, band] = [known, 'all'];
    if (band !== 'tight_25') f = f.concat(unknown);
  }

  const months = (c: Pool) => yearsBetween(c.date, now) * 12;
  let recency: M1['recency'] = 'all';
  if (f.filter((c) => months(c) <= 36).length >= MIN_COMPS) [f, recency] = [f.filter((c) => months(c) <= 36), '36mo'];
  else if (f.filter((c) => months(c) <= 60).length >= MIN_COMPS) [f, recency] = [f.filter((c) => months(c) <= 60), '60mo'];

  const scheme = schemeKey(subject.address);
  const scored = f.map((c) => {
    const same = subject.isUnit && schemeKey(c.address) === scheme;
    return { ...c, same, score: 1 + (same ? 100 : 0) + 20 + Math.max(0, 15 - (c.dist ?? 99) * 5) };
  });
  scored.sort((a, b) => b.score - a.score || (a.dist ?? 99) - (b.dist ?? 99));
  let ranked = scored;
  if (subject.isUnit && ranked.filter((c) => c.same).length >= MIN_COMPS) ranked = ranked.filter((c) => c.same);
  ranked = ranked.slice(0, MAX_COMPS);
  if (ranked.length < MIN_COMPS) return null;

  const adjusted = ranked.map((c) => {
    const r = !subject.isUnit && subject.landSqm && c.land ? clip(subject.landSqm / c.land, ...LAND_RATIO) : 1;
    const db = subject.baths && c.baths != null ? Math.round(subject.baths) - Math.round(c.baths) : 0;
    const y = yearsBetween(c.date, now);
    const recencyW = y === Infinity ? 0.4 : Math.exp((-y * Math.LN2) / RECENCY_HALFLIFE_YEARS);
    const landW = !subject.isUnit && subject.landSqm && !c.land ? 0.5 : 1;
    return { ...c, adj: c.price * r * (1 + BATH_HEDONIC * db), w: c.score * recencyW * landW };
  });

  // Tukey fence, only once there is enough to tell an outlier from a spread.
  let keep = adjusted;
  if (adjusted.length >= 6) {
    const x = adjusted.map((c) => c.adj).sort((a, b) => a - b);
    const n = x.length;
    const q1 = x[Math.floor((n - 1) * 0.25)];
    const q3 = x[Math.floor((n - 1) * 0.75)];
    const iqr = q3 - q1;
    const fenced = adjusted.filter((c) => c.adj >= q1 - 1.5 * iqr && c.adj <= q3 + 1.5 * iqr);
    if (fenced.length >= MIN_COMPS) keep = fenced;
  }

  const sorted = [...keep].sort((a, b) => a.adj - b.adj);
  const total = sorted.reduce((n, c) => n + c.w, 0);
  let acc = 0;
  let mid = sorted[sorted.length - 1].adj;
  for (const c of sorted) {
    acc += c.w;
    if (acc >= total / 2) {
      mid = c.adj;
      break;
    }
  }

  const unknownLand = keep.filter((c) => !c.land).length;
  let confidence: Confidence =
    keep.length >= 5 && band === 'tight_25' && recency === '36mo' && bathRule === 'exact' ? 'High' : 'Medium';
  if (!subject.isUnit && subject.landSqm && unknownLand * 2 > keep.length) confidence = 'Low';
  if (!bedsKnown) confidence = 'Low';

  const sales: OfferComparable[] = sorted.map((c) => ({
    address: c.address,
    price: c.price,
    adjusted: Math.round(c.adj),
    date: c.date,
    distKm: c.dist,
    beds: c.beds,
    baths: c.baths,
    landSqm: c.land,
  }));
  return { mid: Math.round(mid), n: keep.length, confidence, band, recency, bathRule, unknownLand, bedsKnown, sales };
}

// ── Method 3: the property's own last sale ─────────────────────────────────

const MONTHS: Record<string, string> = { Jan: '01', Feb: '02', Mar: '03', Apr: '04', May: '05', Jun: '06', Jul: '07', Aug: '08', Sep: '09', Oct: '10', Nov: '11', Dec: '12' };
/** "Apr 2017" → "2017-04". */
const monthOf = (s: string | null | undefined): string | null => {
  const [m, y] = (s ?? '').split(' ');
  return MONTHS[m] && /^\d{4}$/.test(y ?? '') ? `${y}-${MONTHS[m]}` : null;
};

function lastSettlement(input: OfferInput): { price: number; date: string } | null {
  const A = input.indicative?.price ?? null;
  const gnaf = input.subject.gnafId;

  // The property's own resales from the suburb feed: month → price.
  const resold = new Map<string, number>();
  for (const p of input.repeatSales ?? []) {
    if (!gnaf || p.gnaf_pid !== gnaf) continue;
    const buy = monthOf(p.buy_date), sell = monthOf(p.sell_date);
    if (buy && pos(p.buy_price)) resold.set(buy, p.buy_price!);
    if (sell && pos(p.sell_price)) resold.set(sell, p.sell_price!);
  }

  const settled = (type: string | null | undefined) => {
    const t = (type ?? '').trim().toLowerCase();
    return !t || /^(sold|settled)/.test(t);
  };
  // A settlement in its own history with no price takes it from the resales
  // record for the same month. (10 Towers St: "Sold 2017-04-28", no price;
  // the suburb's resales have it selling for $1,675,000 that April.)
  const rows: { price: number; date: string }[] = input.saleHistory
    .filter((h) => settled(h.type) && yearsBetween(h.date, input.now) !== Infinity)
    .map((h) => ({ price: pos(h.price) ?? resold.get(h.date.slice(0, 7)) ?? 0, date: h.date }))
    .filter((h) => h.price > MIN_SALE_PRICE);
  // Resales the history does not list at all still count.
  for (const [month, price] of resold) {
    if (!rows.some((r) => r.date.slice(0, 7) === month)) rows.push({ price, date: `${month}-15` });
  }
  const usable = rows
    // A "sale" at the advertised price inside a year is the campaign, not a settlement.
    .filter((h) => !(A && Math.abs(h.price - A) / A <= 0.03 && yearsBetween(h.date, input.now) < 1))
    .sort((a, b) => b.date.localeCompare(a.date));
  return usable[0] ?? null;
}

/** Why there is no usable last sale, in the property's own terms. */
function noSaleReason(input: OfferInput): string {
  const unpriced = input.saleHistory.find((h) => /^(sold|settled)/i.test(h.type ?? '') && !pos(h.price));
  return unpriced
    ? `Its last recorded sale, in ${unpriced.date.slice(0, 4)}, has no published price.`
    : 'No earlier sale is on record for this address, so there is no past price to bring forward. Common for homes that have not changed hands in the data’s history.';
}

// ── The window ─────────────────────────────────────────────────────────────

/**
 * How a window's confidence is shown. "Low" is never printed: the level is kept
 * for genuinely thin data, and says so in those words.
 */
export function confidenceText(c: Confidence): 'High confidence' | 'Medium confidence' | 'Limited data' {
  return c === 'High' ? 'High confidence' : c === 'Medium' ? 'Medium confidence' : 'Limited data';
}

/** How one method's standing is shown: its confidence decides its weight. */
export function methodStanding(c: Confidence): 'High' | 'Medium' | 'Lower weight' {
  return c === 'Low' ? 'Lower weight' : c;
}

export function ratingLabel(score: number): RatingLabel {
  return score >= 85 ? 'Strong value' : score >= 65 ? 'Fair price' : score >= 45 ? 'Priced high' : 'Overpriced';
}

/**
 * 70 = the indicative price matches the evidence. Each 1% under adds 2.5 points
 * (100 at 12% under); each 1% over takes 3 (0 at about 23% over).
 */
export function rateGap(gap: number): number {
  return gap >= 0 ? 70 - 300 * gap : 70 - 250 * gap;
}

/** The rating any price earns against the evidence. Thin evidence is held halfway to 70. */
export function rateOffer(price: number, basis: RateBasis): number {
  const r = rateGap((price - basis.evidence) / basis.evidence);
  return Math.round(clip(basis.thin ? 70 + (r - 70) / 2 : r, 0, 100));
}

/** The price at which an offer's rating falls to `score` — the inverse of rateOffer. */
export function priceForRating(score: number, basis: RateBasis): number {
  const raw = basis.thin ? 70 + 2 * (score - 70) : score;
  const gap = raw >= 70 ? -(raw - 70) / 250 : (70 - raw) / 300;
  return Math.round(basis.evidence * (1 + gap));
}

export function offerWindow(input: OfferInput): OfferWindow | null {
  const { subject, now } = input;
  const ind = input.indicative && input.indicative.price > 0 ? input.indicative : null;
  const A = ind?.price ?? 0;
  const dom = input.daysOnMarket;
  const sdom = input.suburbDaysOnMarket;
  const stale = !!(A && dom != null && sdom != null && sdom > 0 && dom >= STALE_MULTIPLE * sdom);

  // The agency's own record of selling above its guide, where it has one.
  let used = A;
  let indicativeNote: string | null = null;
  const aq = input.agentQuoting;
  if (
    ind?.source === 'agent' &&
    !stale &&
    aq &&
    (aq.underquote_rate_pct ?? 0) >= 50 &&
    (aq.median_deviation_pct ?? 0) > 0
  ) {
    used = Math.round(A * (1 + aq.median_deviation_pct! / 100));
    indicativeNote = `${aq.agency_name ?? 'The agency'} typically sells ${aq.median_deviation_pct}% above its guide, so the guide is read as ${short(used)}.`;
  }
  const share = !A ? 0 : stale ? INDICATIVE_SHARE.stale : INDICATIVE_SHARE[ind!.source];

  const m1 = method1(input);
  const prior = lastSettlement(input);

  const methods: OfferMethod[] = [];
  methods.push(
    m1
      ? {
          key: 'M1',
          name: 'Recent comparable sales',
          mid: m1.mid,
          confidence: m1.confidence,
          why: m1.bedsKnown
            ? `${m1.n} settled sales nearby with the same bedrooms${subject.isUnit ? '' : ' and similar land'}, adjusted to this property.`
            : `${m1.n} settled sales nearby of the same type${subject.isUnit ? '' : ' with similar land'}. No bedroom count is published for this property, so it carries less weight.`,
          weight: 0,
        }
      : { key: 'M1', name: 'Recent comparable sales', mid: null, confidence: 'Low', why: 'Too few closely matching sales nearby to rely on.', weight: 0 },
  );

  // The automated estimate models the house the records describe. After an
  // update, a high-spec renovation or a rebuild it is valuing the old one
  // (41 Bailey St: still 4 bed / 2 bath, $3.95m, against a 5-bed new build).
  const houseIn = input.house ?? null;
  const improved = houseIn?.finish === 'updated' || houseIn?.finish === 'high_spec' || houseIn?.finish === 'new_build';
  const avmStale = improved && (houseIn?.finish === 'new_build' || houseIn?.changed_since_sale === 'yes');
  const avmDoubtful = improved && !avmStale && houseIn?.changed_since_sale === 'unknown';
  const avm = pos(input.avm?.predicted_price);
  if (avm && avmStale) {
    methods.push({
      key: 'M2',
      name: 'Automated estimate',
      mid: null,
      confidence: 'Low',
      why: "The model's estimate values the house as it was before the work, so it is not used.",
      weight: 0,
      setAside: true,
    });
  } else if (avm) {
    const off = m1 ? Math.abs(avm - m1.mid) / m1.mid : null;
    let confidence: Confidence = off == null ? 'Medium' : off <= 0.04 ? 'High' : off <= 0.08 ? 'Medium' : 'Low';
    let why = m1
      ? `An automated model estimate, ${pct(off!)} ${avm >= m1.mid ? 'above' : 'below'} the comparable sales.`
      : 'An automated model estimate.';
    if (A && Math.abs(avm - A) / A <= 0.02) {
      confidence = 'Low';
      why += ' It sits on the advertised figure, so may be echoing it rather than testing it.';
    }
    if (prior && avm < prior.price) {
      confidence = 'Low';
      why += ' It is below what the property last sold for.';
    }
    if (avmDoubtful) {
      confidence = 'Low';
      why += ' It may predate the work you described, so it carries the lowest weight.';
    }
    if (m1 && off! > AVM_DROP_PCT) {
      methods.push({
        key: 'M2',
        name: 'Automated estimate',
        mid: null,
        confidence: 'Low',
        why: `The model's estimate is ${pct(off!)} ${avm >= m1.mid ? 'above' : 'below'} the comparable sales, too far to rely on, so it is not used.`,
        weight: 0,
        setAside: true,
      });
    } else methods.push({ key: 'M2', name: 'Automated estimate', mid: avm, confidence, why, weight: 0 });
  } else {
    methods.push({ key: 'M2', name: 'Automated estimate', mid: null, confidence: 'Low', why: 'No automated estimate available.', weight: 0 });
  }

  const growth = horizons(input.priceGrowth);
  if (prior && growth && !input.newBuild) {
    const y = yearsBetween(prior.date, now);
    const lift = indexPct(growth, y);
    const v = Math.round(prior.price * (1 + lift));
    let confidence: Confidence = y <= 4 ? 'High' : y <= 10 ? 'Medium' : 'Low';
    let why = `Sold for ${short(prior.price)} in ${prior.date.slice(0, 4)}, carried forward at the area's growth since (${lift >= 0 ? '+' : ''}${pct(lift)}).`;
    if (m1 && Math.abs(v - m1.mid) / m1.mid > FAR_PCT && (!A || Math.abs(v - A) / A > FAR_PCT)) {
      confidence = 'Low';
      why += " Well away from today's comparable sales — it may have been renovated, or the area moved differently.";
    }
    if (stale && v > A) {
      confidence = 'Low';
      why += ' The market has not paid this while it has been listed for less.';
    }
    methods.push({ key: 'M3', name: 'Its last sale, brought forward', mid: v, confidence, why, weight: 0 });
  } else {
    methods.push({
      key: 'M3',
      name: 'Its last sale, brought forward',
      mid: null,
      confidence: 'Low',
      why: input.newBuild
        ? 'Its last sale was of the previous building.'
        : prior
          ? 'No published growth series to carry its last sale forward.'
          : noSaleReason(input),
      weight: 0,
    });
  }

  // ── What the buyer told us about the house ──────────────────────────────
  const house = input.house ?? null;
  const changed =
    house?.changed_since_sale === 'yes' &&
    (house.finish === 'updated' || house.finish === 'high_spec' || house.finish === 'new_build');
  // A house changed since it last sold is not the house that sale bought.
  const m3 = methods[2];
  if (changed && prior && m3.mid != null) {
    m3.mid = null;
    m3.confidence = 'Low';
    m3.why = `Last sale ${short(prior.price)} in ${prior.date.slice(0, 4)} is not used. The house has been changed since.`;
  }
  const allowance = houseAllowance(house, !subject.isUnit);
  const m1m = methods[0];
  if (m1 && m1m.mid != null && (allowance.pct !== 0 || house?.site === 'approved')) {
    let adjusted = Math.round(m1.mid * (1 + allowance.pct / 100));
    const heldAtAsk = A > 0 && adjusted > A;
    if (heldAtAsk) adjusted = A;
    const step: Record<Confidence, Confidence> = { High: 'Medium', Medium: 'Low', Low: 'Low' };
    m1m.confidence = house?.site === 'approved' ? 'Low' : allowance.pct !== 0 ? step[m1m.confidence] : m1m.confidence;
    m1m.mid = adjusted;
    const said = allowance.parts.map((p) => `${p.label} ${p.pct > 0 ? '+' : '−'}${Math.abs(p.pct)}%`).join(', ');
    m1m.why =
      `Comparable sales ${short(m1.mid)}. ${said || 'No allowance'} → ${short(adjusted)}. You told us this. We have not inspected it.` +
      (allowance.capped ? ` Other features were noted. The allowance is capped at ${HOUSE_CAP}%.` : '') +
      (heldAtAsk ? ' Held at the asking price.' : '');
  }

  // ── Method 4: the provider's comparable set, valued on its own ──────────
  // The same sales top up Method 1's pool, so Method 4 counts towards the
  // midpoint but never towards the agreement that earns confidence: two
  // methods built partly from the same sales agreeing proves little.
  const cma = input.curatedComps.length ? method1({ ...input, comps: [], suburbSales: [] }) : null;
  if (cma && cma.n >= CMA_MIN) {
    const m4: OfferMethod = {
      key: 'M4',
      name: 'Comparative market analysis',
      mid: cma.mid,
      confidence: cma.confidence,
      why: `The ${cma.n} sales most closely matched to this home on its features, each adjusted to it.`,
      weight: 0,
    };
    if (allowance.pct !== 0 || house?.site === 'approved') {
      let adjusted = Math.round(cma.mid * (1 + allowance.pct / 100));
      if (A > 0 && adjusted > A) adjusted = A;
      const step: Record<Confidence, Confidence> = { High: 'Medium', Medium: 'Low', Low: 'Low' };
      m4.confidence = house?.site === 'approved' ? 'Low' : step[m4.confidence];
      m4.mid = adjusted;
      m4.why += ` Matched sales ${short(cma.mid)}, with the same allowance for the house as Method 1 → ${short(adjusted)}.`;
    }
    methods.push(m4);
  } else {
    methods.push({
      key: 'M4',
      name: 'Comparative market analysis',
      mid: null,
      confidence: 'Low',
      why: 'Too few closely matched sales to value it on their own.',
      weight: 0,
    });
  }

  const live = methods.filter((m) => m.mid != null);
  if (!live.length && !A) return null;
  // The methods that stand on their own evidence: what confidence is judged on.
  const independent = live.filter((m) => m.key !== 'M4');

  const tw = live.reduce((n, m) => n + CONF_WEIGHT[m.confidence], 0);
  const evidence = live.length ? live.reduce((n, m) => n + (CONF_WEIGHT[m.confidence] / tw) * m.mid!, 0) : null;
  for (const m of live) m.weight = ((1 - share) * CONF_WEIGHT[m.confidence]) / tw;

  let mid = Math.round(evidence != null ? share * used + (1 - share) * evidence : used);
  // A price the market has had months to take and declined is the ceiling.
  const capped = stale && mid > A;
  if (capped) mid = A;

  const spread = live.length ? Math.max(...live.map((m) => Math.abs(m.mid! - mid) / mid)) : 0;
  const strength = evidenceScore(methods.filter((m) => m.key !== 'M4'), m1, subject);
  const thin = strength < 50;

  // Confidence follows what actually predicted error on 777 held-out sales:
  // how many methods produced a figure and how strong the evidence is — not how
  // far apart they sit, since windows whose methods disagreed were as accurate
  // as Medium ones (6–9% typical error). Low — shown as "Limited data" — is
  // kept for a single method on thin evidence, which erred 13–15%.
  let confidence: Confidence;
  let halfWidth: number;
  const several = independent.length >= 2;
  if (A) {
    confidence = several && spread <= 0.1 ? 'High' : several || strength >= 45 ? 'Medium' : 'Low';
    // Three-quarters of the spread: the full spread overstated the error.
    halfWidth = clip(spread * 0.75, HALF_MIN, confidence === 'Low' ? HALF_MAX_LOW : HALF_MAX);
  } else {
    confidence =
      strength >= 55 && spread <= 0.1 && independent.some((m) => m.confidence !== 'Low')
        ? 'High'
        : several || strength >= 45
          ? 'Medium'
          : 'Low';
    const base = confidence === 'High' ? NO_PRICE_HALF.strong : confidence === 'Medium' ? NO_PRICE_HALF.medium : NO_PRICE_HALF.thin;
    halfWidth = clip(Math.max(base, spread * 0.75), base, NO_PRICE_HALF_MAX);
  }
  // A renovated or new house whose estimate and last sale both describe the
  // old one rests on the comparables plus the buyer's description alone: say
  // so, and widen the window to match.
  const describedOnly = independent.length === 1 && independent[0].key === 'M1' && allowance.pct >= FINISH_PCT.updated;
  if (describedOnly) {
    confidence = 'Low';
    halfWidth = Math.max(halfWidth, A ? DESCRIBED_HALF : NO_PRICE_HALF.thin);
  }

  // ── The rating ──────────────────────────────────────────────────────────
  let rating: OfferWindow['rating'] = null;
  // Rated against the window's midpoint, the figure every other number in the
  // report works from (10 Oct 2026). It carries the asking price at its weight,
  // so a price is measured against the evidence and the market's own guide
  // together, not the comparables alone.
  if (A && evidence != null) {
    const gap = (A - mid) / mid;
    let r = rateGap(gap);
    if (thin) r = 70 + (r - 70) / 2;
    if (stale) r = Math.min(r, 40 - Math.min(20, (20 * (dom! / sdom! - STALE_MULTIPLE)) / 6));
    const score = Math.round(clip(r, 0, 100));
    const g = Math.round(gap * 100);
    rating = {
      score,
      label: ratingLabel(score),
      reason:
        `The ${ind!.source === 'buyer' ? 'price you gave us' : 'listing price'} of ${short(A)} is ` +
        (Math.abs(g) < 1
          ? `level with the offer window midpoint of ${short(mid)}.`
          : `${Math.abs(g)}% ${g > 0 ? 'above' : 'below'} the offer window midpoint of ${short(mid)}.`) +
        (stale ? ` It has been on the market ${dom} days, against a usual ${Math.round(sdom!)}, so the rating is held in the overpriced band.` : '') +
        (thin && !stale ? ' The evidence is thin, so the rating is held nearer the middle.' : ''),
    };
  }

  // Any offer is rated against the same midpoint.
  const rate: RateBasis | null = evidence != null ? { evidence: Math.round(mid), thin } : null;
  // No price to rate: say what offers would rate instead, by the same formula.
  const offerBands: OfferWindow['offerBands'] =
    !A && rate
      ? (() => {
          const p85 = priceForRating(85, rate);
          const p65 = priceForRating(65, rate);
          const p45 = priceForRating(45, rate);
          return [
            { label: 'Strong value', from: null, to: p85 },
            { label: 'Fair price', from: p85, to: p65 },
            { label: 'Priced high', from: p65, to: p45 },
            { label: 'Overpriced', from: p45, to: null },
          ];
        })()
      : null;

  // ── Why this window ─────────────────────────────────────────────────────
  const why: string[] = [];
  const missing = methods.filter((m) => m.mid == null);
  why.push(
    `${live.length} of ${methods.length} methods produced a figure.` +
      (missing.length ? ' ' + missing.map((m) => `${m.name}: ${m.why.charAt(0).toLowerCase() + m.why.slice(1).replace(/\.$/, '')}`).join('; ') + '.' : ''),
  );
  if (live.length) {
    const far = live.reduce((f, m) => (Math.abs(m.mid! - mid) > Math.abs(f.mid! - mid) ? m : f), live[0]);
    why.push(
      `The methods sit up to ${pct(Math.abs(far.mid! - mid) / mid)} from the midpoint (${far.name.toLowerCase()} is furthest out), so the window runs ${pct(halfWidth)} either side.`,
    );
  }
  if (capped) why.push(`Listed for ${dom} days against a usual ${Math.round(sdom!)} for this suburb. The market hasn't paid ${short(A)}, so the window doesn't centre above it.`);
  else if (stale) why.push(`Listed for ${dom} days against a usual ${Math.round(sdom!)} for this suburb, so the listing price counts for less.`);
  if (!A) why.push('There is no asking price, so the window rests on the methods alone and is wider to allow for it.');
  why.push(
    describedOnly
      ? 'Only the comparable sales, adjusted for what you told us about the house, produced a figure: the automated estimate and the last sale describe it before the work. The window is wider to allow for that.'
      : confidence === 'High'
      ? 'The methods agree closely, so this window is narrow and stated with high confidence.'
      : confidence === 'Low'
        ? 'The data here is limited — one method on few matching sales — so the window is wide. Treat the edges as soft.'
        : spread > 0.15
          ? 'The methods sit some way apart, so the window is wider than usual. On past sales, windows like this still held the sale price about 7 times in 10.'
          : 'The methods broadly agree, so this window is stated with medium confidence.',
  );

  return {
    mid,
    low: Math.round(mid * (1 - halfWidth)),
    // "Might be splittable" widens the top only; the midpoint does not move.
    high: house?.site === 'possible' ? Math.max(Math.round(mid * (1 + halfWidth)), Math.round(mid * (1 + SPLIT_TOP))) : Math.round(mid * (1 + halfWidth)),
    halfWidth,
    confidence,
    methods,
    evidence: evidence != null ? Math.round(evidence) : null,
    indicative: ind ? { price: A, source: ind.source, used, weight: share, note: indicativeNote } : null,
    stale: stale ? { days: dom!, suburbDays: Math.round(sdom!) } : null,
    capped,
    rating,
    rate,
    offerBands,
    why,
    comparables: m1?.sales ?? [],
    lastSale: prior,
  };
}

/**
 * How far the evidence can be trusted, 0–100. Internal: it decides whether the
 * rating may make a strong call, and is not printed as a score of its own.
 */
function evidenceScore(
  methods: OfferMethod[],
  m1: M1 | null,
  subject: OfferInput['subject'],
): number {
  const live = methods.filter((m) => m.mid != null);
  const tw = live.reduce((n, m) => n + CONF_WEIGHT[m.confidence], 0);
  const E = live.reduce((n, m) => n + (CONF_WEIGHT[m.confidence] / tw) * m.mid!, 0);
  const coverage = live.reduce((n, m) => n + { High: 10, Medium: 7, Low: 4 }[m.confidence], 0);
  const spread = live.length >= 2 ? Math.max(...live.map((m) => Math.abs(m.mid! - E) / E)) : null;
  const agreement = spread == null ? (live.length ? 8 : 0) : 30 * clip(1 - (spread - 0.05) / 0.25, 0, 1);
  const depth = m1
    ? (15 * Math.min(m1.n, 8)) / 8 + (m1.band === 'tight_25' || m1.band === 'no_subject_area' ? 5 : m1.band === 'med_50' ? 2 : 0)
    : 0;
  const fresh =
    (m1 ? (m1.recency === '36mo' ? 6 : m1.recency === '60mo' ? 3 : 0) : 0) +
    (subject.baths ? 2 : 0) +
    (subject.isUnit || subject.landSqm ? 4 : 0) +
    (m1 && m1.bathRule === 'exact' ? 4 : 0) +
    (m1 && m1.n ? 4 * (1 - m1.unknownLand / m1.n) : 0);
  return Math.round(coverage + agreement + depth + fresh);
}
